import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UsersIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import { documentService } from '../../api/services';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { formatTND } from '../../utils/formatters';

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const PALETTE = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#6366f1', '#14b8a6', '#ec4899'];

type Range = '6m' | '12m' | '24m';

interface MonthlyPoint {
  key: string;
  label: string;
  amount: number;
}

function monthKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function buildMonthlySeries(factures: any[], months: number): MonthlyPoint[] {
  const now = new Date();
  const out: MonthlyPoint[] = [];
  const buckets = new Map<string, number>();
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const k = monthKey(d);
    buckets.set(k, 0);
    out.push({
      key: k,
      label: `${MONTHS_FR[d.getMonth()]}${months > 12 ? ' ' + String(d.getFullYear()).slice(2) : ''}`,
      amount: 0,
    });
  }
  for (const f of factures) {
    if (!f.dateDocument) continue;
    const k = monthKey(new Date(f.dateDocument));
    if (buckets.has(k)) buckets.set(k, (buckets.get(k) || 0) + Number(f.totalHT || 0));
  }
  return out.map((p) => ({ ...p, amount: buckets.get(p.key) || 0 }));
}

export function Analytique() {
  const [range, setRange] = useState<Range>('12m');
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const all = await documentService.getAll({ size: 5000 } as any).then((p) => p.content);
        setDocs(all);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const factures = useMemo(() => docs.filter((d: any) => d.typeDocument === 'FACTURE'), [docs]);

  const months = range === '6m' ? 6 : range === '24m' ? 24 : 12;
  const series = useMemo(() => buildMonthlySeries(factures, months), [factures, months]);

  const totals = useMemo(() => {
    const sum = series.reduce((s, p) => s + p.amount, 0);
    const half = Math.floor(series.length / 2);
    const first = series.slice(0, half).reduce((s, p) => s + p.amount, 0);
    const second = series.slice(half).reduce((s, p) => s + p.amount, 0);
    const mom =
      series.length >= 2
        ? (series[series.length - 1].amount - series[series.length - 2].amount) /
          Math.max(1, series[series.length - 2].amount)
        : 0;
    const yoyDelta = first > 0 ? (second - first) / first : 0;
    const avgInvoice = factures.length > 0 ? sum / factures.length : 0;
    return { sum, mom, yoyDelta, avgInvoice };
  }, [series, factures]);

  const topClients = useMemo(() => {
    const m = new Map<string, { name: string; total: number; count: number }>();
    for (const f of factures) {
      const k = String((f as any).clientId);
      const e = m.get(k) || { name: (f as any).clientRaisonSociale || 'Inconnu', total: 0, count: 0 };
      e.total += Number((f as any).totalHT || 0);
      e.count += 1;
      m.set(k, e);
    }
    return Array.from(m.values()).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [factures]);

  const concentration = useMemo(() => {
    const top5 = topClients.slice(0, 5).reduce((s, c) => s + c.total, 0);
    return totals.sum > 0 ? top5 / totals.sum : 0;
  }, [topClients, totals.sum]);

  const topArticles = useMemo(() => {
    const m = new Map<string, { name: string; total: number; qty: number }>();
    for (const f of factures) {
      for (const l of (f as any).lignes || []) {
        const k = l.articleId ? `a-${l.articleId}` : `d-${l.designation || ''}`;
        const e = m.get(k) || { name: l.designation || 'Article', total: 0, qty: 0 };
        e.total += Number(l.montantHT || 0);
        e.qty += Number(l.quantite || 0);
        m.set(k, e);
      }
    }
    return Array.from(m.values()).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [factures]);

  const statutBreakdown = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of factures) {
      const k = (f as any).statutPaiement || 'INCONNU';
      m.set(k, (m.get(k) || 0) + 1);
    }
    const labels: Record<string, string> = {
      PAYEE: 'Payée',
      PAYEE_PARTIELLE: 'Partielle',
      NON_PAYEE: 'Non payée',
      ENVOYEE: 'Envoyée',
      BROUILLON: 'Brouillon',
      IMPAYEE: 'Impayée',
      ANNULEE: 'Annulée',
    };
    return Array.from(m.entries()).map(([k, v], i) => ({
      name: labels[k] || k,
      value: v,
      fill: PALETTE[i % PALETTE.length],
    }));
  }, [factures]);

  // Simple 3-month forecast based on a 6-month moving average. We surface the
  // method explicitly to set expectations — this is not ML.
  const forecast = useMemo(() => {
    const last6 = series.slice(-6).map((p) => p.amount);
    const avg = last6.length > 0 ? last6.reduce((s, v) => s + v, 0) / last6.length : 0;
    const lastDate = new Date();
    const out: { mois: string; montant: number; estime: boolean }[] = [];
    for (const p of series.slice(-6)) {
      out.push({ mois: p.label, montant: p.amount, estime: false });
    }
    for (let i = 1; i <= 3; i++) {
      const d = new Date(lastDate.getFullYear(), lastDate.getMonth() + i, 1);
      out.push({
        mois: `${MONTHS_FR[d.getMonth()]} (proj.)`,
        montant: Math.round(avg),
        estime: true,
      });
    }
    return out;
  }, [series]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Analytique</h1>
          <p className="page-subtitle">Tendances, segmentation et prévisions sur vos ventes</p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
          {(['6m', '12m', '24m'] as Range[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-md font-medium transition ${
                range === r ? 'bg-emerald-500 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {r === '6m' ? '6 mois' : r === '12m' ? '12 mois' : '24 mois'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="CA total" value={formatTND(totals.sum)} accent="text-emerald-600" />
        <Kpi
          label="Variation MoM"
          value={`${(totals.mom * 100).toFixed(1)}%`}
          accent={totals.mom >= 0 ? 'text-emerald-600' : 'text-red-600'}
          trendUp={totals.mom >= 0}
        />
        <Kpi
          label="Évolution sur la période"
          value={`${(totals.yoyDelta * 100).toFixed(1)}%`}
          accent={totals.yoyDelta >= 0 ? 'text-emerald-600' : 'text-red-600'}
          trendUp={totals.yoyDelta >= 0}
        />
        <Kpi label="Panier moyen" value={formatTND(totals.avgInvoice)} accent="text-gray-700" />
      </div>

      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">CA mensuel</h2>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: any) => formatTND(Number(v))} />
              <Bar dataKey="amount" name="CA HT" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <UsersIcon className="h-4 w-4" />
              Top clients
            </h2>
            <span className="text-xs text-gray-500">
              Top 5 = {(concentration * 100).toFixed(0)}% du CA
            </span>
          </div>
          {topClients.length === 0 ? (
            <p className="text-sm text-gray-500">Pas assez de données.</p>
          ) : (
            <ul className="space-y-2">
              {topClients.map((c, i) => {
                const pct = totals.sum > 0 ? (c.total / totals.sum) * 100 : 0;
                return (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <span className="flex-shrink-0 w-6 text-xs text-gray-400 font-mono text-right">{i + 1}.</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-gray-500">{c.count} fact.</span>
                    <span className="w-24 text-right tnd-amount font-medium">{formatTND(c.total)}</span>
                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <CubeIcon className="h-4 w-4" />
            Top articles
          </h2>
          {topArticles.length === 0 ? (
            <p className="text-sm text-gray-500">Pas assez de données.</p>
          ) : (
            <ul className="space-y-2">
              {topArticles.map((a, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className="flex-shrink-0 w-6 text-xs text-gray-400 font-mono text-right">{i + 1}.</span>
                  <span className="flex-1 truncate">{a.name}</span>
                  <span className="text-gray-500">{a.qty.toLocaleString('fr-TN', { maximumFractionDigits: 0 })}u.</span>
                  <span className="w-24 text-right tnd-amount font-medium">{formatTND(a.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Répartition par statut</h2>
          {statutBreakdown.length === 0 ? (
            <p className="text-sm text-gray-500">Pas de données.</p>
          ) : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={statutBreakdown}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={80}
                    label={(e: any) => `${e.name}: ${e.value}`}
                  >
                    {statutBreakdown.map((s, i) => (
                      <Cell key={i} fill={s.fill} />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700">Prévision 3 mois</h2>
            <span className="text-[10px] text-gray-400">Moyenne mobile 6 mois</span>
          </div>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={forecast} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="mois" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: any) => formatTND(Number(v))} />
                <Line
                  type="monotone"
                  dataKey="montant"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={(p: any) => {
                    const cx = Number(p.cx) || 0;
                    const cy = Number(p.cy) || 0;
                    return (
                      <circle
                        key={`dot-${p.index}`}
                        cx={cx}
                        cy={cy}
                        r={4}
                        fill={p.payload?.estime ? '#fff' : '#10b981'}
                        stroke="#10b981"
                        strokeWidth={2}
                        strokeDasharray={p.payload?.estime ? '2 2' : '0'}
                      />
                    );
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-[11px] text-gray-400">
            Cette projection est une heuristique (moyenne sur 6 mois) et non un modèle prédictif.
          </p>
        </div>
      </div>
    </div>
  );
}

interface KpiProps {
  label: string;
  value: string;
  accent?: string;
  trendUp?: boolean;
}
const Kpi: React.FC<KpiProps> = ({ label, value, accent = 'text-gray-700', trendUp }) => (
  <div className="card">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</div>
        <div className={`mt-1 text-lg font-semibold ${accent}`}>{value}</div>
      </div>
      {trendUp === true && <ArrowTrendingUpIcon className="h-5 w-5 text-emerald-500" />}
      {trendUp === false && <ArrowTrendingDownIcon className="h-5 w-5 text-red-500" />}
    </div>
  </div>
);

export default Analytique;
